import { useEffect, useState } from "react";
import {
  getPaymentProvider,
  setPaymentProvider,
  type PaymentProvider,
  STRIPE_DISABLED,
} from "@/lib/payment-provider";

/**
 * Selector visible (solo sandbox) para alternar entre Stripe y ONVO durante pruebas.
 * - Persiste la elección en localStorage.
 * - Si la URL tiene ?provider=..., ese valor manda y se muestra en modo "URL override".
 * - Se oculta automáticamente en producción live (cuando VITE_PAYMENTS_CLIENT_TOKEN es pk_live_).
 */
export function PaymentProviderSwitcher() {
  const [provider, setProvider] = useState<PaymentProvider>("stripe");
  const [hasQueryOverride, setHasQueryOverride] = useState(false);

  useEffect(() => {
    setProvider(getPaymentProvider());
    if (typeof window !== "undefined") {
      const q = new URLSearchParams(window.location.search).get("provider");
      setHasQueryOverride(q === "onvopay" || q === "stripe");
    }
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<PaymentProvider>).detail;
      if (detail === "onvopay" || detail === "stripe") setProvider(detail);
    };
    window.addEventListener("payment-provider-changed", onChange);
    return () => window.removeEventListener("payment-provider-changed", onChange);
  }, []);

  // Hide in live production builds — sandbox-only tool.
  const token = import.meta.env.VITE_PAYMENTS_CLIENT_TOKEN as string | undefined;
  if (token && token.startsWith("pk_live_")) return null;
  if (STRIPE_DISABLED) return null;

  function pick(next: PaymentProvider) {
    if (hasQueryOverride && typeof window !== "undefined") {
      // Quitar el override de URL para que la elección manual prevalezca.
      const url = new URL(window.location.href);
      url.searchParams.delete("provider");
      window.history.replaceState({}, "", url.toString());
      setHasQueryOverride(false);
    }
    setPaymentProvider(next);
    setProvider(next);
  }

  return (
    <div className="w-full bg-violet-950/40 border-b border-violet-500/30 px-4 py-2 text-xs">
      <div className="mx-auto max-w-7xl flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-violet-200">
          <span className="font-semibold uppercase tracking-wider text-[10px]">
            Pasarela
          </span>
          <span className="text-violet-100/70">
            actual:{" "}
            <span className="font-mono font-bold text-violet-50">{provider}</span>
            {hasQueryOverride && (
              <span className="ml-2 rounded bg-violet-500/30 px-1.5 py-0.5 text-[10px] text-violet-100">
                URL override
              </span>
            )}
          </span>
        </div>
        <div className="flex items-center gap-1 rounded-lg bg-violet-900/60 p-0.5">
          <button
            type="button"
            onClick={() => pick("stripe")}
            className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
              provider === "stripe"
                ? "bg-violet-500 text-white"
                : "text-violet-200 hover:bg-violet-800/60"
            }`}
          >
            Stripe
          </button>
          <button
            type="button"
            onClick={() => pick("onvopay")}
            className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
              provider === "onvopay"
                ? "bg-violet-500 text-white"
                : "text-violet-200 hover:bg-violet-800/60"
            }`}
          >
            ONVO
          </button>
        </div>
      </div>
    </div>
  );
}
